import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../frontend/backend/navbar.js";
import Chatbox from "../frontend/backend/chatbox.js";
import HeaderRight from "../frontend/backend/header_right.js";
import LeftSidebar from "../frontend/backend/leftSidebar.js";
import axios from "axios";
import apiConfig from "../apiConfig";

/* ─── inline styles ─────────────────────────────────────────── */
const S = {
  page: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    fontSize: 13,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
    flexWrap: "wrap",
    gap: 8,
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
    margin: 0,
    color: "#000",
    letterSpacing: "-0.2px",
  },
  badge: (color) => ({
    display: "inline-block",
    padding: "1px 8px",
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    background: color === "green" ? "#14532d" : "#7f1d1d",
    color: color === "green" ? "#000" : "#f87171",
    marginLeft: 6,
  }),

  // ── filter bar ──
  filterBar: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 10,
    padding: "8px 10px",
    marginBottom: 10,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "flex-end",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: "1 1 100px",
    minWidth: 100,
  },
  filterLabel: {
    fontSize: 9,
    fontWeight: 600,
    color: "#000",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  filterInput: {
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 6,
    color: "#000",
    padding: "5px 8px",
    fontSize: 12,
    outline: "none",
    width: "100%",
  },
  filterSelect: {
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 6,
    color: "#000",
    padding: "5px 8px",
    fontSize: 12,
    outline: "none",
    cursor: "pointer",
    width: "100%",
  },
  btnReset: {
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 6,
    color: "#000",
    padding: "5px 12px",
    fontSize: 12,
    cursor: "pointer",
    alignSelf: "flex-end",
    whiteSpace: "nowrap",
  },

  // ── stat cards ──
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 6,
    marginBottom: 12,
  },
  statCard: (accent) => ({
    background: "rgba(255,255,255,0.04)",
    border: `1px solid ${accent}33`,
    borderRadius: 8,
    padding: "8px 10px",
  }),
  statLabel: {
    fontSize: 9,
    fontWeight: 600,
    color: "#000",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    marginBottom: 2,
  },
  statValue: (color) => ({
    fontSize: 15,
    fontWeight: 800,
    color,
    letterSpacing: "-0.3px",
  }),
  statSub: {
    fontSize: 9,
    color: "#000",
    marginTop: 1,
  },

  // ── table (desktop) ──
  tableWrap: {
    overflowX: "auto",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.07)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: (sortable) => ({
    padding: "7px 10px",
    textAlign: "left",
    fontSize: 10,
    fontWeight: 700,
    color: "#000",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    background: "rgba(0,0,0,0.25)",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    cursor: sortable ? "pointer" : "default",
    whiteSpace: "nowrap",
    userSelect: "none",
  }),
  td: {
    padding: "6px 10px",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
    color: "#000",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  trHover: {
    background: "rgba(255,255,255,0.025)",
  },
  pill: (win) => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    background: win ? "#14532d" : "#7f1d1d",
    color: win ? "#000" : "#f87171",
  }),
  profitCell: (val) => ({
    fontWeight: 700,
    fontSize: 12,
    color: val >= 0 ? "#000" : "#f87171",
  }),
  userLink: {
    color: "#38bdf8",
    fontFamily: "monospace",
    fontSize: 11,
    textDecoration: "none",
    background: "rgba(56,189,248,0.08)",
    padding: "1px 6px",
    borderRadius: 5,
    border: "1px solid rgba(56,189,248,0.15)",
  },
  emptyRow: {
    textAlign: "center",
    padding: "28px 0",
    color: "#475569",
    fontSize: 12,
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 2px 2px",
    flexWrap: "wrap",
    gap: 6,
  },
  pageInfo: { fontSize: 11, color: "#000" },
  pageBtn: (active) => ({
    padding: "4px 9px",
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.1)",
    background: active ? "#3b82f6" : "rgba(255,255,255,0.05)",
    color: active ? "#fff" : "#000",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: active ? 700 : 400,
  }),

  // ── mobile cards ──
  mobileCard: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 8,
    padding: "7px 9px",
    marginBottom: 6,
  },
  mobileTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  mobileDate: { fontSize: 10, color: "#000" },
  mobileAmt: { fontSize: 13, fontWeight: 800, color: "#000" },
  mobileMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
    fontSize: 10,
    color: "#000",
    marginBottom: 3,
  },
  mobileMatch: { fontSize: 11, color: "#000", marginBottom: 3 },
  mobileFoot: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 11,
  },
};

const PAGE_SIZE = 20; // change this number to show more/fewer rows per page

const LuckyExchangeSummery = () => {
  const { id } = useParams();
  const isSingleUser = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState([]);
  const [totalWin, setTotalWin] = useState(0);
  const [totalBet, setTotalBet] = useState(0);

  // ── filters ──
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");   // all | win | lost
  const [sportFilter, setSportFilter] = useState("all");
  const [backLayFilter, setBackLayFilter] = useState("all"); // all | b | l
  const [search, setSearch] = useState("");                   // match / market name

  // ── sort ──
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  // ── pagination ──
  const [page, setPage] = useState(1);

  const [hoveredRow, setHoveredRow] = useState(null);

  /* ── fetch ────────────────────────────────────────────────── */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;

        const url = isSingleUser
          ? `${apiConfig.baseURL}/api/agent/LuckyExchange/${id}`
          : `${apiConfig.baseURL}/api/agent/LuckyExchange`;

        const response = await axios.get(url, { params });
        setGameData(response.data.data || []);
        setTotalWin(response.data.totalWin || 0);
        setTotalBet(response.data.totalBet || 0);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, startDate, endDate]);

  /* ── helpers ──────────────────────────────────────────────── */
  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const options = { hour: "numeric", minute: "numeric", hour12: true };
    const t = date.toLocaleString("en-US", options).toLowerCase();
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = String(date.getFullYear()).slice(-2);
    return `${t}  ${d}.${m}.${y}`;
  };

  const getBet = (item) => (item.bets?.length > 0 ? item.bets[0] : {});
  const amt = (val) => ((val || 0) / 100); // raw amount is stored ×100

  /* ── derived sport list for filter dropdown ───────────────── */
  const sportOptions = useMemo(() => {
    const sports = new Set(gameData.map((r) => getBet(r).sport_name).filter(Boolean));
    return ["all", ...Array.from(sports).sort()];
  }, [gameData]);

  /* ── client-side filtering ────────────────────────────────── */
  const filtered = useMemo(() => {
    return gameData.filter((item) => {
      const bet = getBet(item);
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (sportFilter !== "all" && bet.sport_name !== sportFilter) return false;
      if (backLayFilter !== "all" && bet.backlay !== backLayFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const match = (bet.competitor_name?.join(" ") || "").toLowerCase();
        const market = (bet.market_name || "").toLowerCase();
        const user = (item.user_id || item.ext_player_id || "").toLowerCase();
        if (!match.includes(q) && !market.includes(q) && !user.includes(q)) return false;
      }
      return true;
    });
  }, [gameData, statusFilter, sportFilter, backLayFilter, search]);

  /* ── client-side sort ─────────────────────────────────────── */
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av, bv;
      if (sortKey === "amount") { av = amt(a.amount); bv = amt(b.amount); }
      else if (sortKey === "profit") { av = parseFloat(a.trade_profit); bv = parseFloat(b.trade_profit); }
      else { av = new Date(a.createdAt); bv = new Date(b.createdAt); }
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  }, [filtered, sortKey, sortDir]);

  /* ── pagination ───────────────────────────────────────────── */
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
    setPage(1);
  };

  const resetFilters = () => {
    setStatusFilter("all");
    setSportFilter("all");
    setBackLayFilter("all");
    setSearch("");
    setPage(1);
  };

  const arrow = (key) =>
    sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : " ↕";

  /* ── stat computations on filtered set ───────────────────── */
  const filteredWin = filtered.filter((r) => r.status === "win").reduce((s, r) => s + amt(r.amount), 0);
  const filteredLost = filtered.filter((r) => r.status === "lost").reduce((s, r) => s + amt(r.amount), 0);
  const filteredPL = filteredWin - filteredLost;
  const winCount = filtered.filter((r) => r.status === "win").length;
  const lostCount = filtered.filter((r) => r.status === "lost").length;
  const winRate = filtered.length > 0 ? ((winCount / filtered.length) * 100).toFixed(1) : "0.0";

  return (
    <>
      <Navbar />
      <Chatbox />
      <HeaderRight />
      <LeftSidebar />

      <style>{`
        @media (max-width: 767px) {
          .lex-table-wrap { display: none !important; }
          .lex-mobile-list { display: block !important; }
          .lex-stats { grid-template-columns: repeat(2, 1fr) !important; }
          .lex-filter-group { flex: 1 1 45% !important; }
        }
        @media (min-width: 768px) {
          .lex-table-wrap { display: block !important; }
          .lex-mobile-list { display: none !important; }
          .lex-stats { grid-template-columns: repeat(5, 1fr) !important; }
        }
      `}</style>

      <div className="content-body" style={S.page}>
        <div className="container-fluid">
          <div className="card col-lg-12" style={{ background: "transparent", border: "none" }}>
            <div className="container" style={{ maxWidth: "100%", padding: "0 4px" }}>

              {/* ── Header ── */}
              <div style={S.header}>
                <div>
                  <h2 style={S.title}>
                    Lucky 
                    <span style={{ color: "#000", fontWeight: 400, fontSize: 13 }}>
                      {" — "}{isSingleUser ? "User Report" : "All Users"}
                    </span>
                  </h2>
                </div>
                {isSingleUser && (
                  <Link to={`/ninewicket-summery/${id}`} className="btn btn-primary shadow btn-xs">
                     9Wicket
                  </Link>
                )}
              </div>

              {/* ── Filter Bar ── */}
              <div style={S.filterBar}>
                <div style={S.filterGroup} className="lex-filter-group">
                  <span style={S.filterLabel}>Start Date</span>
                  <input type="date" style={S.filterInput} value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
                </div>
                <div style={S.filterGroup} className="lex-filter-group">
                  <span style={S.filterLabel}>End Date</span>
                  <input type="date" style={S.filterInput} value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
                </div>
                <div style={S.filterGroup} className="lex-filter-group">
                  <span style={S.filterLabel}>Status</span>
                  <select style={S.filterSelect} value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                    <option value="all">All</option>
                    <option value="win">Win</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
                <div style={S.filterGroup} className="lex-filter-group">
                  <span style={S.filterLabel}>Sport</span>
                  <select style={S.filterSelect} value={sportFilter}
                    onChange={(e) => { setSportFilter(e.target.value); setPage(1); }}>
                    {sportOptions.map((s) => (
                      <option key={s} value={s}>{s === "all" ? "All Sports" : s}</option>
                    ))}
                  </select>
                </div>
                <div style={S.filterGroup} className="lex-filter-group">
                  <span style={S.filterLabel}>Back / Lay</span>
                  <select style={S.filterSelect} value={backLayFilter}
                    onChange={(e) => { setBackLayFilter(e.target.value); setPage(1); }}>
                    <option value="all">All</option>
                    <option value="b">Back</option>
                    <option value="l">Lay</option>
                  </select>
                </div>
                <div style={S.filterGroup} className="lex-filter-group">
                  <span style={S.filterLabel}>Search</span>
                  <input type="text" placeholder="Match, market, user…" style={S.filterInput}
                    value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                </div>
                <button style={S.btnReset} onClick={resetFilters}>✕ Reset</button>
              </div>

              {/* ── Stat Cards ── */}
              <div style={S.statsRow} className="lex-stats">
                <div style={S.statCard("#22c55e")}>
                  <div style={S.statLabel}>Profit / Loss</div>
                  <div style={S.statValue(filteredPL >= 0 ? "#000" : "#f87171")}>
                    {filteredPL.toLocaleString()}
                  </div>
                  <div style={S.statSub}>BDT (filtered)</div>
                </div>
                <div style={S.statCard("#3b82f6")}>
                  <div style={S.statLabel}>Total Win Amount</div>
                  <div style={S.statValue("#000")}>{filteredWin.toLocaleString()}</div>
                  <div style={S.statSub}>{winCount} bets won</div>
                </div>
                <div style={S.statCard("#ef4444")}>
                  <div style={S.statLabel}>Total Lost Amount</div>
                  <div style={S.statValue("#f87171")}>{filteredLost.toLocaleString()}</div>
                  <div style={S.statSub}>{lostCount} bets lost</div>
                </div>
                <div style={S.statCard("#a855f7")}>
                  <div style={S.statLabel}>Win Rate</div>
                  <div style={S.statValue("#c084fc")}>{winRate}%</div>
                  <div style={S.statSub}>{filtered.length} total bets</div>
                </div>
                <div style={S.statCard("#f59e0b")}>
                  <div style={S.statLabel}>Showing</div>
                  <div style={S.statValue("#fbbf24")}>{filtered.length}</div>
                  <div style={S.statSub}>of {gameData.length} records</div>
                </div>
              </div>

              {/* ── Table / Cards ── */}
              {loading ? (
                <div style={{ textAlign: "center", padding: 30, color: "#475569", fontSize: 12 }}>
                  Loading…
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="lex-table-wrap" style={S.tableWrap}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th(true)} onClick={() => handleSort("createdAt")}>
                            Date{arrow("createdAt")}
                          </th>
                          {!isSingleUser && <th style={S.th(false)}>User</th>}
                          <th style={S.th(true)} onClick={() => handleSort("amount")}>
                            Amount{arrow("amount")}
                          </th>
                          <th style={S.th(false)}>Status</th>
                          <th style={S.th(false)}>Sport</th>
                          <th style={S.th(false)}>Match</th>
                          <th style={S.th(false)}>Market</th>
                          <th style={S.th(false)}>Outcome</th>
                          <th style={S.th(false)}>Odds</th>
                          <th style={S.th(false)}>B/L</th>
                          <th style={S.th(true)} onClick={() => handleSort("profit")}>
                            Trade P/L{arrow("profit")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginated.length > 0 ? (
                          paginated.map((item, index) => {
                            const bet = getBet(item);
                            const isWin = item.status === "win";
                            const profit = parseFloat(item.trade_profit) || 0;
                            return (
                              <tr key={index}
                                onMouseEnter={() => setHoveredRow(index)}
                                onMouseLeave={() => setHoveredRow(null)}
                                style={hoveredRow === index ? S.trHover : {}}>
                                <td style={S.td}>
                                  <span style={{ color: "#000", fontSize: 11 }}>
                                    {formatDate(item.createdAt)}
                                  </span>
                                </td>
                                {!isSingleUser && (
                                  <td style={S.td}>
                                    <Link
                                      to={`/lucky-exchange-summery/${item.ext_player_id}`}
                                      style={S.userLink}>
                                      {item.user_id || item.mobile || (item.ext_player_id || "").slice(-8)}
                                    </Link>
                                  </td>
                                )}
                                <td style={{ ...S.td, fontWeight: 700, color: "#000" }}>
                                  {amt(item.amount).toLocaleString()}
                                </td>
                                <td style={S.td}>
                                  <span style={S.pill(isWin)}>{isWin ? "Win" : "Lost"}</span>
                                </td>
                                <td style={S.td}>
                                  <span style={{
                                    fontSize: 10, fontWeight: 600,
                                    color: bet.sport_name === "Cricket" ? "#34d399"
                                         : bet.sport_name === "Soccer"  ? "#60a5fa" : "#a78bfa"
                                  }}>
                                    {bet.sport_name || "-"}
                                  </span>
                                </td>
                                <td style={{ ...S.td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {bet.competitor_name?.join(" vs ") || "-"}
                                </td>
                                <td style={{ ...S.td, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", color: "#000" }}>
                                  {bet.market_name || "-"}
                                </td>
                                <td style={{ ...S.td, color: "#000" }}>{bet.outcome_name || "-"}</td>
                                <td style={{ ...S.td, fontWeight: 700, color: "#fbbf24" }}>{bet.odds || "-"}</td>
                                <td style={S.td}>
                                  <span style={{
                                    fontSize: 10, fontWeight: 700,
                                    color: bet.backlay === "b" ? "#38bdf8" : "#f472b6"
                                  }}>
                                    {bet.backlay === "b" ? "BACK" : bet.backlay === "l" ? "LAY" : "-"}
                                  </span>
                                </td>
                                <td style={{ ...S.td, ...S.profitCell(profit) }}>
                                  {profit >= 0 ? "+" : ""}{profit.toLocaleString()}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={isSingleUser ? 10 : 11} style={S.emptyRow}>
                              No records found for the selected filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="lex-mobile-list" style={{ display: "none" }}>
                    {paginated.length > 0 ? (
                      paginated.map((item, index) => {
                        const bet = getBet(item);
                        const isWin = item.status === "win";
                        const profit = parseFloat(item.trade_profit) || 0;
                        return (
                          <div style={S.mobileCard} key={index}>
                            <div style={S.mobileTop}>
                              <span style={S.mobileDate}>{formatDate(item.createdAt)}</span>
                              <span style={S.mobileAmt}>{amt(item.amount).toLocaleString()}</span>
                            </div>
                            <div style={S.mobileMeta}>
                              <span style={S.pill(isWin)}>{isWin ? "Win" : "Lost"}</span>
                              <span style={{
                                color: bet.sport_name === "Cricket" ? "#34d399"
                                     : bet.sport_name === "Soccer"  ? "#60a5fa" : "#a78bfa"
                              }}>
                                {bet.sport_name || "-"}
                              </span>
                              <span style={{ color: bet.backlay === "b" ? "#38bdf8" : "#f472b6", fontWeight: 700 }}>
                                {bet.backlay === "b" ? "BACK" : bet.backlay === "l" ? "LAY" : "-"}
                              </span>
                              <span style={{ color: "#fbbf24", fontWeight: 700 }}>Odds {bet.odds || "-"}</span>
                            </div>
                            <div style={S.mobileMatch}>
                              {bet.competitor_name?.join(" vs ") || "-"}
                              {bet.market_name ? ` · ${bet.market_name}` : ""}
                            </div>
                            <div style={S.mobileFoot}>
                              {!isSingleUser ? (
                                <Link to={`/lucky-exchange-summery/${item.ext_player_id}`} style={S.userLink}>
                                  {item.user_id || item.mobile || (item.ext_player_id || "").slice(-8)}
                                </Link>
                              ) : <span />}
                              <span style={S.profitCell(profit)}>
                                {profit >= 0 ? "+" : ""}{profit.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={S.emptyRow}>No records found for the selected filters.</div>
                    )}
                  </div>

                  {/* ── Pagination ── */}
                  {sorted.length > 0 && (
                    <div style={S.pagination}>
                      <span style={S.pageInfo}>
                        Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length}
                      </span>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button style={S.pageBtn(false)} onClick={() => setPage(1)} disabled={page === 1}>«</button>
                        <button style={S.pageBtn(false)} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const p = Math.max(1, Math.min(page - 2 + i, totalPages - 4 + i));
                          return (
                            <button key={p} style={S.pageBtn(p === page)} onClick={() => setPage(p)}>{p}</button>
                          );
                        })}
                        <button style={S.pageBtn(false)} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
                        <button style={S.pageBtn(false)} onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
                      </div>
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LuckyExchangeSummery;